// Indici operativi di "calore" / impegno di una missione di picking.
// Helper puro (nessuna dipendenza da React/DOM), quindi facilmente testabile e
// riutilizzabile sia dalla tabella pick-list sia da future viste.
//
//  · IRG (Indice Riscaldamento Ginocchia) ← quanto fa camminare l'operatore,
//    guidato dal numero di ubicazioni (`locations`).
//  · IBR (Indice Braccia) ← carico di prelievo, guidato dai colli assegnati
//    (`totalQtyAssigned`).
//  · ICM (Indice Calore Missione) ← media pesata di IRG, IBR e di una penalità
//    di saturazione che scatta solo quando i colli superano `maxQtyPerMission`.
//
// Tutti gli indici sono interi 0-100. La frammentazione (articoli/ubicazione)
// NON è arrotondata: serve come dato informativo nel tooltip.

/** Soglia di default: colli massimi consigliati per missione. */
export const MAX_QTY_PER_MISSION_DEFAULT = 100;

/**
 * Ubicazioni che saturano IRG a 100. ≈ una missione "piena" di sole camminate;
 * con densità tipica di pochi colli/ubicazione corrisponde grosso modo a una
 * missione da `MAX_QTY_PER_MISSION_DEFAULT` colli. Sovrascrivibile via options.
 */
export const REF_LOCATIONS_DEFAULT = 40;

/** Pesi della media ICM (proposta della spec). Somma = 1. */
export const PICKING_INDEX_WEIGHTS = { irg: 0.45, ibr: 0.35, saturation: 0.2 } as const;

export type HeatLevel = 'cold' | 'fresh' | 'normal' | 'warm' | 'hot';

/** Emoji per fascia ICM, condivisa da tutti i badge "calore missione". */
export const HEAT_EMOJI: Record<HeatLevel, string> = {
  cold: '🧊', fresh: '🌿', normal: '✅', warm: '☀️', hot: '🔥',
};

export interface PickingIndexInput {
  /** Numero di ubicazioni distinte della distinta (`distinct_locations`). */
  locations: number;
  /** Quantità totale / colli assegnati (`qty_total`). */
  totalQtyAssigned: number;
}

export interface PickingIndexOptions {
  /** Colli massimi consigliati per missione (config per magazzino). Default 100. */
  maxQtyPerMission?: number;
  /** Ubicazioni che saturano IRG. Default 40. */
  refLocations?: number;
  /** Pesi della media ICM. Default {@link PICKING_INDEX_WEIGHTS}. */
  weights?: { irg: number; ibr: number; saturation: number };
}

export interface PickingIndexes {
  irg: number;
  ibr: number;
  icm: number;
  /** Articoli per ubicazione (qty/ubicazioni). 0 se ubicazioni=0. Non arrotondato. */
  fragmentation: number;
  heatLevel: HeatLevel;
  shouldSplitMission: boolean;
}

const clamp = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n));

/**
 * Frammentazione = colli ÷ ubicazioni. Ritorna 0 in caso di divisione per zero
 * (nessuna ubicazione) o di assenza di colli. Estratta qui per essere riusata
 * dalla cella "Frammentazione" della tabella senza duplicare la regola.
 */
export function computeFragmentation(totalQtyAssigned: number, locations: number): number {
  if (locations <= 0 || totalQtyAssigned <= 0) return 0;
  return totalQtyAssigned / locations;
}

/** Mappa un ICM 0-100 sulla fascia di calore. */
export function heatLevelOf(icm: number): HeatLevel {
  if (icm <= 20) return 'cold';
  if (icm <= 40) return 'fresh';
  if (icm <= 60) return 'normal';
  if (icm <= 80) return 'warm';
  return 'hot';
}

/**
 * Calcola IRG/IBR/ICM + fascia di calore + flag "da frammentare" per una distinta.
 *
 * Regole di robustezza (da spec):
 *  - locations = 0  → fragmentation 0 (niente divisione per zero).
 *  - totalQtyAssigned = 0 → tutti gli indici a 0 (missione "vuota").
 *  - tutti gli indici sono interi arrotondati.
 */
export function calculatePickingIndexes(
  input: PickingIndexInput,
  options: PickingIndexOptions = {},
): PickingIndexes {
  const refLoc = options.refLocations && options.refLocations > 0
    ? options.refLocations : REF_LOCATIONS_DEFAULT;
  const maxQty = options.maxQtyPerMission && options.maxQtyPerMission > 0
    ? options.maxQtyPerMission : MAX_QTY_PER_MISSION_DEFAULT;
  const w = options.weights ?? PICKING_INDEX_WEIGHTS;

  const locations = Math.max(0, input.locations || 0);
  const qty = Math.max(0, input.totalQtyAssigned || 0);

  const fragmentation = computeFragmentation(qty, locations);

  // Nessun collo assegnato → missione vuota: indici a 0, fredda, niente split.
  if (qty <= 0) {
    return { irg: 0, ibr: 0, icm: 0, fragmentation: 0, heatLevel: 'cold', shouldSplitMission: false };
  }

  const irg = clamp(Math.round((locations / refLoc) * 100), 0, 100);
  const ibr = clamp(Math.round((qty / maxQty) * 100), 0, 100);

  // Penalità di saturazione: scatta solo oltre la soglia max colli/missione.
  const saturationRatio = Math.max(0, qty - maxQty) / maxQty;
  const saturationPenalty = clamp(saturationRatio * 100, 0, 100);

  const icm = clamp(
    Math.round(irg * w.irg + ibr * w.ibr + saturationPenalty * w.saturation),
    0,
    100,
  );

  return {
    irg,
    ibr,
    icm,
    fragmentation,
    heatLevel: heatLevelOf(icm),
    shouldSplitMission: qty > maxQty || icm >= 80,
  };
}
